import { useEffect, useState } from "react";

const API =
  process.env.REACT_APP_API_URL ||
  (typeof window !== "undefined" && window.location.hostname !== "localhost"
    ? "https://admin.renerg.co.rw"
    : "http://localhost:4000");

let cache = null;
let cacheAt = 0;
let inflight = null;

export function mediaUrl(url) {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("data:") || url.startsWith("blob:")) return url;
  if (url.startsWith("/static/") || url.startsWith("static/")) return url.startsWith("/") ? url : `/${url}`;
  return `${API}${url.startsWith("/") ? url : `/${url}`}`;
}

export function prepareHtml(html, linkTo) {
  if (!html) return "";
  let out = String(html)
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
    .replace(/\son\w+=(["']).*?\1/gi, "")
    .replace(/(src|href)=(["'])(\/uploads\/[^"']+)\2/gi, (_, attr, quote, path) => `${attr}=${quote}${API}${path}${quote}`);
  out = out.replace(/(<a[^>]*>\s*)?<img\b([^>]*)>(\s*<\/a>)?/gi, (full, openA, attrs) => {
    if (/src=/i.test(attrs) && /src=["']\/uploads\//i.test(attrs) && !/src=["']https?:/i.test(attrs)) {
      attrs = attrs.replace(/src=(["'])(\/uploads\/[^"']+)\1/i, (_, q, path) => `src=${q}${API}${path}${q}`);
    }
    const img = `<img${attrs.startsWith(" ") ? attrs : ` ${attrs}`}>`;
    if (!linkTo) return img;
    return `<a href="${linkTo}" class="cms-image-link">${img}</a>`;
  });
  return out;
}

export function sectionBlocks(content = [], section) {
  return (content || []).filter((item) => item.section === section);
}

export function blockData(block) {
  const raw = block?.data;
  if (!raw || typeof raw !== "object") return {};
  return raw;
}

export async function fetchSiteContent(force = false) {
  if (force) {
    cache = null;
    inflight = null;
  }
  if (cache && Date.now() - cacheAt < 400) return cache;
  if (inflight) return inflight;
  inflight = fetch(`${API}/api/public/content?t=${Date.now()}`, { cache: "no-store" })
    .then((res) => {
      if (!res.ok) throw new Error("Failed to load site content");
      return res.json();
    })
    .then((data) => {
      cache = data;
      cacheAt = Date.now();
      inflight = null;
      return data;
    })
    .catch((err) => {
      inflight = null;
      throw err;
    });
  return inflight;
}

const emptySite = { content: [], services: [], projects: [], pages: [], settings: {} };
const listeners = new Set();
let liveStarted = false;

function notify(data) {
  listeners.forEach((fn) => fn(data));
}

function startLiveUpdates() {
  if (liveStarted || typeof window === "undefined") return;
  liveStarted = true;
  const load = (force = false) =>
    fetchSiteContent(force)
      .then((data) => notify(data))
      .catch(() => {});
  load(true);
  window.addEventListener("focus", () => load(true));
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") load(true);
  });
  window.setInterval(() => load(true), 8000);
}

export function useSiteData() {
  const [data, setData] = useState(cache || emptySite);

  useEffect(() => {
    listeners.add(setData);
    if (cache) setData(cache);
    startLiveUpdates();
    return () => {
      listeners.delete(setData);
    };
  }, []);

  return data;
}

export async function askSite(question, history = []) {
  const res = await fetch(`${API}/api/public/ask`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      question,
      history: history.slice(-6).map((item) => ({ role: item.role, text: item.text })),
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || "Could not answer");
  return data;
}

export async function sendEnquiry(payload) {
  const res = await fetch(`${API}/api/public/enquiries`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || "Could not send message");
  return data;
}

export function trackVisit() {
  try {
    let sessionId = sessionStorage.getItem("renerg_sid");
    if (!sessionId) {
      sessionId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem("renerg_sid", sessionId);
    }
    fetch(`${API}/api/public/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        eventType: "page_view",
        path: window.location.pathname + window.location.hash || "/",
        referrer: document.referrer || null,
        device: window.innerWidth < 768 ? "mobile" : "desktop",
        sessionId,
      }),
    }).catch(() => {});
  } catch {
    // ignore tracking errors
  }
}
